/**
 * validation.ts — Gemeinsame Validierungsregeln
 *
 * Passwort-Regeln:
 *   - 8–72 Zeichen (bcrypt-Limit)
 *   - Mindestens 1 Ziffer
 *   - Mindestens 1 Kleinbuchstabe, 1 Großbuchstabe
 *   - Mindestens 1 Sonderzeichen (nicht Buchstabe, Ziffer oder Leerzeichen)
 *   - Nicht alle Zeichen gleich
 *
 * Email-Regeln:
 *   - Grundlegendes Format `name@domain.tld`
 *   - Keine Domain aus EMAIL_DOMAIN_BLACKLIST (Wegwerf-Provider)
 *     inkl. Subdomain-Match (z.B. `foo.mailinator.com` → erkannt)
 *
 * @module lib/validation
 */

import { EMAIL_DOMAIN_BLACKLIST } from './emailBlacklist.js';

// ════════════════════════════════════════════════════════════════════
// Passwort
// ════════════════════════════════════════════════════════════════════

/**
 * Prüft Passwort gegen zentrale Regeln:
 *   - 8–72 Zeichen (bcrypt-Limit: Supabase schneidet stillschweigend bei
 *     72 ab; längere Passwörter würden beim Login inkonsistent behandelt)
 *   - Mindestens 1 Ziffer
 *   - Mindestens 1 Kleinbuchstabe
 *   - Mindestens 1 Großbuchstabe
 *   - Mindestens 1 Sonderzeichen (nicht Buchstabe, Ziffer oder Leerzeichen)
 *   - Nicht trivial (nicht alle Zeichen gleich)
 *
 * Akzeptiert explizit auch null/undefined und leere Strings — diese werden
 * als "ungültig" (false) behandelt. Damit können Caller die Funktion
 * defensiv aufrufen, ohne vorher selbst auf Existenz prüfen zu müssen.
 *
 * @param pw - Passwort-Kandidat (string, null oder undefined)
 * @returns true wenn gültig, false sonst
 */
export function validatePassword(pw: string | null | undefined): boolean {
  if (!pw || pw.length < 8) return false;
  if (pw.length > 72) return false;              // bcrypt-Limit
  if (!/\d/.test(pw)) return false;              // min. 1 Ziffer
  if (!/[a-z]/.test(pw)) return false;           // min. 1 Kleinbuchstabe
  if (!/[A-Z]/.test(pw)) return false;           // min. 1 Großbuchstabe
  if (!/[^a-zA-Z0-9\s]/.test(pw)) return false;  // min. 1 Sonderzeichen
  if (/^(.)\1+$/.test(pw)) return false;         // nicht alle gleich ("aaaaaaaa")
  return true;
}

// ════════════════════════════════════════════════════════════════════
// Email
// ════════════════════════════════════════════════════════════════════

/**
 * Mögliche Ergebnisse der Email-Validierung.
 * Discriminated Union — erlaubt präzise i18n-Fehlermeldung am Call-Site.
 */
export type EmailValidationResult = 'ok' | 'invalid' | 'blacklisted';

/**
 * Grundlegender Regex-Check für Email-Adressen.
 * Bewusst permissiv — vollständige RFC 5322-Validierung ist im Frontend
 * weder möglich noch sinnvoll; finale Prüfung passiert über den
 * E-Mail-Versand (Double-Opt-In via Supabase Auth).
 *
 * @param email - Kandidaten-String
 * @returns true wenn das Format plausibel ist
 */
function hasValidEmailShape(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

/**
 * Extrahiert die Domain (Teil nach dem letzten "@") aus einer Email.
 * Gibt null zurück wenn keine Domain gefunden wird.
 *
 * @param email - Email-Adresse
 * @returns Kleingeschriebene Domain oder null
 */
function extractDomain(email: string): string | null {
  const at = email.lastIndexOf('@');
  if (at === -1 || at === email.length - 1) return null;
  return email.slice(at + 1).toLowerCase();
}

/**
 * Prüft, ob die Domain einer Email-Adresse auf der Blacklist steht.
 *
 * Vergleich ist case-insensitive. Berücksichtigt Subdomains: `mailinator.com`
 * in der Blacklist blockt auch `foo.mailinator.com` (wichtig weil viele
 * Wegwerf-Provider wie Mailinator und 33mail Wildcard-Subdomains anbieten).
 *
 * Akzeptiert auch null/undefined (→ gibt false zurück, da ohne Email
 * keine Blacklist-Verletzung vorliegen kann). Bei ungültigem Format → false.
 *
 * @param email - Email-Adresse
 * @returns true wenn Domain geblockt ist, false sonst
 */
export function isBlacklistedEmail(
  email: string | null | undefined,
): boolean {
  if (!email) return false;
  const domain = extractDomain(email.trim());
  if (!domain) return false;
  // Exakt-Match
  if (EMAIL_DOMAIN_BLACKLIST.includes(domain)) return true;
  // Subdomain-Match: z.B. `foo.mailinator.com` → prüfe `mailinator.com`
  // Schleife schneidet von links immer ein Label ab, bis nur noch TLD übrig.
  const parts = domain.split('.');
  for (let i = 1; i < parts.length - 1; i++) {
    const parent = parts.slice(i).join('.');
    if (EMAIL_DOMAIN_BLACKLIST.includes(parent)) return true;
  }
  return false;
}

/**
 * Vollständige Email-Validierung: Format + Blacklist.
 *
 * Rückgabe als Discriminated Union, damit der Caller gezielt den
 * passenden i18n-Key wählen kann (`errEmail` vs. `errEmailBlacklist`).
 *
 * @param email - Kandidaten-String (auch null/undefined akzeptiert)
 * @returns 'ok' | 'invalid' | 'blacklisted'
 */
export function validateEmail(
  email: string | null | undefined,
): EmailValidationResult {
  if (!email || !email.trim()) return 'invalid';
  const trimmed = email.trim();
  if (!hasValidEmailShape(trimmed)) return 'invalid';
  if (isBlacklistedEmail(trimmed)) return 'blacklisted';
  return 'ok';
}
